import { useEffect, useMemo, useState } from "react";

import dayjs from "@shared/dayjs";
import Dropdown from "@shared/v2/Dropdown";
// Imported directly so the overrides below extend the design-system styling
// instead of replacing it — `Dropdown` merges `styles` per key, so any key we
// define here would otherwise drop its border/shadow/padding.
import dropdownStyles from "@shared/v2/Dropdown/Dropdown.styles";
import DatePicker from "@shared/v2/DatePicker";

import { getCanonicalOptions, getDefaultOption } from "./getCanonicalOptions";
import { getPreviousYearOptions } from "./getPreviousYearOptions";
import { resolveDateRange } from "./resolveDateRange";
import { resolvePreviousYearRange } from "./resolvePreviousYearRange";
import type {
  DateRange,
  DateRangeMeta,
  DateRangeOption,
  DateRangeOptionValue,
  PreviousYearSegmentOption,
  PreviousYearSegmentValue,
  ReportDateConfig,
} from "./types";

const ISO = "YYYY-MM-DD";
const DISPLAY = "MM/DD/YYYY";

/**
 * `Dropdown.styles` is untyped JS, so give its shape a local contract rather
 * than letting `any` leak into the overrides below.
 */
type SelectStyleFn = (provided: Record<string, unknown>, state: unknown) => Record<string, unknown>;

const BASE_STYLES = (dropdownStyles as unknown as () => Record<string, SelectStyleFn>)();

/**
 * Enough room for the widest canonical labels ("Last 14 Days", "Last Quarter",
 * "September") plus the control's padding and caret, so the closed control is
 * the same size on every report and doesn't resize as the selection changes.
 */
const MIN_CONTROL_WIDTH = 180;

/**
 * react-select sizes the menu to the *control*, and the control shrinks to fit
 * the selected label — so picking a short option like "Today" used to clip
 * longer options in the open menu.
 *
 * Fixed in two layers:
 *  - `control.minWidth` keeps the closed control wide enough for any label.
 *  - `menu` sizes to its own content (never narrower than the control), so the
 *    list stays readable even if labels grow or a consumer narrows the control.
 *
 * Applied as `styles` rather than a Tailwind class so it composes predictably
 * with consumer `dropdownClassName` widths (different CSS properties) instead
 * of depending on Tailwind's stylesheet ordering.
 */
const DATE_RANGE_SELECT_STYLES: Record<string, SelectStyleFn> = {
  control: (provided, state) => ({
    ...BASE_STYLES.control(provided, state),
    minWidth: MIN_CONTROL_WIDTH,
  }),
  menu: (provided, state) => ({
    ...BASE_STYLES.menu(provided, state),
    width: "max-content",
    minWidth: "100%",
  }),
  option: (provided, state) => ({
    ...BASE_STYLES.option(provided, state),
    whiteSpace: "nowrap",
  }),
};

const toDisplay = (iso: string | null): string => {
  if (!iso) return "";
  const parsed = dayjs(iso, ISO);
  if (!parsed.isValid()) return "";
  return parsed.format(DISPLAY);
};

const toIso = (display: string): string | null => {
  if (!display) return null;
  const parsed = dayjs(display, DISPLAY);
  if (!parsed.isValid()) return null;
  return parsed.format(ISO);
};

/**
 * Clamp a hydrated ISO date to today when `disableFutureDates` is on. Only
 * used for `initialCustomRange` on mount; runtime user input is validated,
 * not silently rewritten.
 */
const clampToToday = (iso: string | null, disableFutureDates: boolean): string | null => {
  if (!iso || !disableFutureDates) return iso;
  const parsed = dayjs(iso, ISO);
  if (!parsed.isValid()) return iso;
  const today = dayjs().startOf("day");
  return parsed.isAfter(today) ? today.format(ISO) : iso;
};

/**
 * Track the selected option, tolerating values that aren't in `options`.
 *
 * Unrecognized values (stale tokens persisted in localStorage, legacy numeric
 * `days=` values, options removed by a config change) fall back to
 * `fallbackValue` — the report's configured default — and only then to the
 * first option. Without the fallback these land on `options[0]`, which for the
 * full canonical list is "All Time": the slowest possible query and never what
 * the report asked for.
 */
const useNormalizedSelection = <TOption extends { value: string }>(
  options: TOption[],
  initialValue: TOption["value"] | undefined,
  fallbackValue?: TOption["value"],
) => {
  const resolveFallback = () =>
    options.find((o) => o.value === fallbackValue)?.value ?? options[0].value;

  const [selectedValue, setSelectedValue] = useState<TOption["value"]>(
    () => options.find((o) => o.value === initialValue)?.value ?? resolveFallback(),
  );

  if (!options.some((o) => o.value === selectedValue)) {
    setSelectedValue(resolveFallback());
  }

  const selected = useMemo<TOption>(
    () => options.find((o) => o.value === selectedValue) ?? options[0],
    [options, selectedValue],
  );

  return [selectedValue, selected, setSelectedValue] as const;
};

interface BaseProps {
  /**
   * Fired whenever the selected option, resolved date range, or year (in
   * previous-year mode) changes. Also fires once on mount.
   *
   * `meta.isValid` is `false` when Custom mode has inverted endpoints or a
   * partial range. Consumers should gate their fetch on `meta.isValid`; the
   * callback still fires so parent state stays in sync with the UI.
   */
  onChange?: (value: string, dateRange: DateRange, meta: DateRangeMeta) => void;
  className?: string;
  dropdownClassName?: string;
  /**
   * Optional react-select `components` map forwarded to the inner `Dropdown`.
   * Use this to match a page's existing dropdown styling (e.g. the Business
   * Dashboard `CustomControl` / `CustomDropdownIndicator` set).
   */
  components?: Record<string, unknown>;
  "data-cy"?: string;
}

interface StandardProps extends BaseProps {
  mode?: "standard";
  /** Per-report configuration controlling which options appear and Custom behavior. */
  config?: ReportDateConfig;
  /** Initial option to select. Defaults to `config.defaultOption` or the first option. */
  initialValue?: DateRangeOptionValue;
  /** Initial custom range. Used only when `initialValue === "custom"`. */
  initialCustomRange?: DateRange;
  year?: never;
}

interface PreviousYearProps extends BaseProps {
  mode: "previous-year";
  /** The prior calendar year being navigated. */
  year: number;
  /** Initial segment to select. Defaults to `january`. */
  initialValue?: PreviousYearSegmentValue;
  config?: never;
  initialCustomRange?: never;
}

export type DateRangeDropdownProps = StandardProps | PreviousYearProps;

/**
 * Shared date-range dropdown used by every standardized report.
 *
 * Two modes:
 *  - `standard`: canonical relative options (All Time, Today, Last N Days,
 *    This/Last Month, This/Last Quarter, This/Last Year) plus optional Custom.
 *  - `previous-year`: months + quarters for a prior calendar year.
 *
 * `initialValue` and `initialCustomRange` are read only at mount. To reset
 * the picker for a different report, pass a stable `key` so React remounts.
 * `config` fields (`includeOptions`/`excludeOptions`/`disableFutureDates`)
 * are live; if a runtime change removes the selected option — or `initialValue`
 * is a value this report doesn't offer — the component reconciles to
 * `config.defaultOption`, falling back to the first available option.
 */
const DateRangeDropdown = (props: DateRangeDropdownProps) => {
  if (props.mode === "previous-year") return <PreviousYearDropdown {...props} />;
  return <StandardDropdown {...props} />;
};

const StandardDropdown = ({
  config,
  initialValue,
  initialCustomRange,
  onChange,
  className,
  dropdownClassName,
  components,
  "data-cy": dataCy,
}: StandardProps) => {
  // Key the options memo on primitive content of `config` rather than the
  // object reference — inline `config={{ ... }}` props would otherwise
  // cache-bust every render.
  const includeOptionsKey = config?.includeOptions?.join("|");
  const excludeOptionsKey = config?.excludeOptions?.join("|");
  const configDefaultOption = config?.defaultOption;

  const options = useMemo<DateRangeOption[]>(
    () => getCanonicalOptions(config),
    [includeOptionsKey, excludeOptionsKey, configDefaultOption],
  );

  const disableFutureDates = !!config?.disableFutureDates;

  const defaultOption = getDefaultOption(config);
  const resolvedInitialValue = initialValue ?? defaultOption;

  const [selectedValue, selected, setSelectedValue] = useNormalizedSelection(
    options,
    resolvedInitialValue,
    defaultOption,
  );

  // `initialCustomRange` is only honored when the initial selection is Custom.
  // Otherwise a persisted custom range could resurrect the moment the user
  // switches to Custom, before they've picked anything new.
  const [customStartIso, setCustomStartIso] = useState<string | null>(() =>
    resolvedInitialValue === "custom"
      ? clampToToday(initialCustomRange?.startAt ?? null, disableFutureDates)
      : null,
  );
  const [customEndIso, setCustomEndIso] = useState<string | null>(() =>
    resolvedInitialValue === "custom"
      ? clampToToday(initialCustomRange?.endAt ?? null, disableFutureDates)
      : null,
  );

  const isCustom = selectedValue === "custom";
  const maxCustomDate = disableFutureDates ? dayjs().format(ISO) : undefined;

  const dateRange = useMemo<DateRange>(() => {
    if (isCustom) return { startAt: customStartIso, endAt: customEndIso };
    return resolveDateRange(selectedValue, { disableFutureDates });
  }, [selectedValue, customStartIso, customEndIso, isCustom, disableFutureDates]);

  const error = useMemo(() => {
    if (!isCustom || !customStartIso || !customEndIso) return "";
    const startDate = dayjs(customStartIso, ISO);
    const endDate = dayjs(customEndIso, ISO);
    if (!startDate.isSameOrBefore(endDate)) {
      return "End date must be on or after start date";
    }

    if (disableFutureDates) {
      const today = dayjs().startOf("day");
      if (startDate.isAfter(today) || endDate.isAfter(today)) {
        return "Dates cannot be in the future";
      }
    }
    return "";
  }, [isCustom, customStartIso, customEndIso, disableFutureDates]);

  useEffect(() => {
    // Partial Custom ranges (user picked one endpoint but not the other)
    // are not safe to fetch. Emit with `meta.isValid: false` so consumers can
    // freeze their state without the parent drifting out of sync.
    const isRangeComplete = !isCustom || (!!customStartIso && !!customEndIso);
    onChange?.(selectedValue, dateRange, { isValid: !error && isRangeComplete, error });
    // `onChange` identity is intentionally excluded from deps to avoid noisy
    // re-emits when consumers pass an inline callback.
  }, [selectedValue, dateRange.startAt, dateRange.endAt, error]);

  return (
    <div
      className={`tw-flex tw-items-center tw-gap-[8px] ${className ?? ""}`}
      data-cy={dataCy}
    >
      <Dropdown
        // @ts-expect-error @shared/v2/Dropdown uses PropTypes, props are not typed
        className={dropdownClassName}
        components={components}
        styles={DATE_RANGE_SELECT_STYLES}
        options={options}
        value={selected}
        onChange={(next: DateRangeOption | null) => {
          if (!next) return;
          // On switch to Custom, seed the DatePickers with the current
          // preset's range so the visible data stays continuous.
          if (next.value === "custom" && !isCustom) {
            const currentRange = resolveDateRange(selectedValue, { disableFutureDates });
            setCustomStartIso(currentRange.startAt);
            setCustomEndIso(currentRange.endAt);
          }
          setSelectedValue(next.value);
        }}
      />
      {isCustom && (
        <>
          <DatePicker
            position="bottom"
            value={toDisplay(customStartIso)}
            onChange={(displayValue: string) => setCustomStartIso(toIso(displayValue))}
            inputProps={{ size: 10, "aria-label": "Custom start date" }}
            // @ts-expect-error @shared/v2/DatePicker uses PropTypes; maxDate is supported at runtime but not in the type surface
            maxDate={maxCustomDate}
          />
          <DatePicker
            position="bottom"
            value={toDisplay(customEndIso)}
            onChange={(displayValue: string) => setCustomEndIso(toIso(displayValue))}
            inputProps={{ size: 10, error, "aria-label": "Custom end date" }}
            // @ts-expect-error @shared/v2/DatePicker uses PropTypes; minDate/maxDate are supported at runtime but not in the type surface
            minDate={customStartIso ?? undefined}
            maxDate={maxCustomDate}
          />
        </>
      )}
    </div>
  );
};

const PreviousYearDropdown = ({
  year,
  initialValue,
  onChange,
  className,
  dropdownClassName,
  components,
  "data-cy": dataCy,
}: PreviousYearProps) => {
  const options = useMemo<PreviousYearSegmentOption[]>(
    () => getPreviousYearOptions(year),
    [year],
  );

  const [selectedValue, selected, setSelectedValue] = useNormalizedSelection(
    options,
    initialValue,
  );

  const dateRange = useMemo<DateRange>(
    () => resolvePreviousYearRange(year, selectedValue),
    [year, selectedValue],
  );

  useEffect(() => {
    onChange?.(selectedValue, dateRange, { isValid: true, error: "" });
  }, [selectedValue, year, dateRange.startAt, dateRange.endAt]);

  return (
    <div className={className} data-cy={dataCy}>
      <Dropdown
        // @ts-expect-error @shared/v2/Dropdown uses PropTypes, props are not typed
        className={dropdownClassName}
        components={components}
        styles={DATE_RANGE_SELECT_STYLES}
        options={options}
        value={selected}
        onChange={(next: PreviousYearSegmentOption | null) => next && setSelectedValue(next.value)}
      />
    </div>
  );
};

export default DateRangeDropdown;
